import { useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Grid,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { MainLayoutContainer } from "components/Layout";
import { useAuthUser } from "hooks/useAuthUser";
import { useVetcoin } from "hooks/useVetcoin";
import { NotificationSeverity } from "helpers/constants";
import { useNotifications } from "hooks/useNotifications";
import { StoreItem, StoreItemType } from "types/vetcoin";
import { ROLES } from "types/user";
import { EquippedAnimationPresentation } from "components/VetStore/EquippedAnimationPresentation";
import { StoreItemPreview } from "components/VetStore/StoreItemPreview";

const rarityColor: Record<string, "default" | "primary" | "secondary" | "warning"> =
  {
    COMMON: "default",
    UNCOMMON: "primary",
    RARE: "secondary",
    EPIC: "warning",
    LEGENDARY: "warning",
  };

const typeLabel: Record<StoreItemType, string> = {
  PET: "VetPets",
  ANIMATION: "Animations",
};

export const VetStore = () => {
  const { user } = useAuthUser();
  const isAdmin = user?.role === ROLES.ADMIN;
  const { notify } = useNotifications();
  const [tab, setTab] = useState<StoreItemType>("PET");
  const [showcaseItem, setShowcaseItem] = useState<StoreItem | null>(null);
  const {
    loading,
    balance,
    storeItems,
    buyStoreItem,
    equipStoreItem,
    isOwned,
    getEquippedByType,
  } = useVetcoin(user?.user_id);

  const filteredItems = useMemo(
    () => storeItems.filter((item) => item.type === tab),
    [storeItems, tab],
  );

  const equippedItem = getEquippedByType(tab);
  const isShowcasing = !!showcaseItem;

  const onBuy = async (item: StoreItem) => {
    if (!user?.user_id) return;
    const result = await buyStoreItem({
      userId: user.user_id,
      item,
      skipVetcoinDebit: isAdmin,
    });
    if (!result.purchased) {
      notify({
        message:
          result.reason === "INSUFFICIENT_BALANCE"
            ? "Not enough Vetcoins for this item."
            : "You already own this item.",
        severity: NotificationSeverity.warning,
      });
      return;
    }
    notify({
      message: `${item.name} adopted successfully.`,
      severity: NotificationSeverity.success,
    });
  };

  const onEquip = async (item: StoreItem) => {
    if (!user?.user_id) return;
    await equipStoreItem({ userId: user.user_id, item });
    notify({
      message: `${item.name} equipped.`,
      severity: NotificationSeverity.success,
    });
  };

  return (
    <MainLayoutContainer
      title="VetStore"
      loading={loading}
      description="Spend your Vetcoins on VetPets and feedback animations."
      buttonActions={
        <Chip
          color="primary"
          label={`Balance: ${balance} Vetcoins`}
          sx={{ fontWeight: 700 }}
        />
      }
    >
      <Tabs
        value={tab}
        onChange={(_, value) => {
          setTab(value);
          setShowcaseItem(null);
        }}
        sx={{ mb: 2 }}
      >
        <Tab value="PET" label={typeLabel.PET} />
        <Tab value="ANIMATION" label={typeLabel.ANIMATION} />
      </Tabs>

      <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="body2">
          Equipped {typeLabel[tab]}:{" "}
          <b>{equippedItem?.store_item?.name || "Nothing equipped"}</b>
        </Typography>
        {isAdmin && tab === "ANIMATION" && (
          <Stack direction="row" spacing={1} alignItems="center">
            {isShowcasing && (
              <Chip label={`Showcasing: ${showcaseItem?.name}`} color="secondary" />
            )}
            <Button
              size="small"
              variant="outlined"
              disabled={!isShowcasing}
              onClick={() => setShowcaseItem(null)}
            >
              Stop Showcase
            </Button>
          </Stack>
        )}
      </Stack>

      <Grid container spacing={2} alignItems="stretch">
        {filteredItems.map((item) => {
          const owned = isOwned(item.store_item_id);
          const isEquipped = equippedItem?.store_item_id === item.store_item_id;
          const canAfford = isAdmin || balance >= item.cost;

          return (
            <Grid item xs={12} sm={6} md={4} lg={3} key={item.store_item_id}>
              <Box
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  p: 2,
                  minHeight: 320,
                  display: "flex",
                  flexDirection: "column",
                  minWidth: 0,
                }}
              >
                <StoreItemPreview item={item} />
                <Stack direction="row" justifyContent="space-between" mb={1}>
                  <Typography
                    variant="h6"
                    sx={{ minWidth: 0, overflowWrap: "anywhere" }}
                  >
                    {item.name}
                  </Typography>
                  <Chip
                    label={item.rarity}
                    size="small"
                    color={rarityColor[item.rarity] || "default"}
                  />
                </Stack>
                <Typography variant="body2" sx={{ mb: 2, overflowWrap: "anywhere" }}>
                  {item.description}
                </Typography>
                <Stack direction="row" justifyContent="space-between" mb={2} gap={1} flexWrap="wrap">
                  <Chip
                    label={isAdmin ? "Free for admins" : `${item.cost} Vetcoins`}
                    color="primary"
                  />
                  {owned && <Chip label="Owned" color="success" />}
                </Stack>
                {!owned ? (
                  <Button
                    fullWidth
                    variant="contained"
                    disabled={!canAfford}
                    onClick={() => onBuy(item)}
                    sx={{ mt: "auto" }}
                  >
                    Adopt
                  </Button>
                ) : (
                  <Button
                    fullWidth
                    variant={isEquipped ? "outlined" : "contained"}
                    disabled={isEquipped}
                    onClick={() => onEquip(item)}
                    sx={{ mt: "auto" }}
                  >
                    {isEquipped ? "Equipped" : "Equip"}
                  </Button>
                )}
                {isAdmin && item.type === "ANIMATION" && (
                  <Button
                    fullWidth
                    variant="text"
                    onClick={() =>
                      setShowcaseItem((current) =>
                        current?.store_item_id === item.store_item_id ? null : item,
                      )
                    }
                    sx={{ mt: 1 }}
                  >
                    {showcaseItem?.store_item_id === item.store_item_id
                      ? "Hide Showcase"
                      : "Showcase"}
                  </Button>
                )}
              </Box>
            </Grid>
          );
        })}
      </Grid>
      {isAdmin && <EquippedAnimationPresentation item={showcaseItem} />}
    </MainLayoutContainer>
  );
};
